import { FC } from "react";
import { Button, Textarea } from "@fluentui/react-components";
import {
  ArrowRight24Filled,
  Dismiss16Regular,
  ErrorCircle12Filled,
  Prompt20Regular,
  Stop24Filled,
} from "@fluentui/react-icons";
import { customTokens } from "../../theme/theme";
import { IconButton } from "../../components/IconButton";
import Typography from "../../components/Typography";
import { useInputBoxStyles } from "./AIClimateAssistant.styles";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import {
  setQuery,
  setShowMore,
  setTogglePromptOverlay,
  setVisibleCount,
  togglePromptDrawer,
} from "../../store/slices/ClimateAssistantSlice";
import { extractLastBotSQL } from "./AIClimateAssistant.config";
import { useIsMobile } from "../../hooks";

const QUERY_MAX_LENGTH = 1500;

interface IInputBox {
  handleSend: VoidFunction;
  handleStop: VoidFunction;
  getPromptsList: (query: string) => Promise<void>;
}

const InputBox: FC<IInputBox> = ({
  handleSend,
  handleStop,
  getPromptsList,
}) => {
  const styles = useInputBoxStyles();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();

  const { query, showMore, loading, displayPromptOverlay, message } =
    useSelector((state: RootState) => state.climateAssistant);

  const sendButton = () => {
    return (
      <IconButton
        icon={
          <ArrowRight24Filled
            color={
              query.length > QUERY_MAX_LENGTH || query.length === 0
                ? customTokens.colorNeutralForegroundDisabled
                : customTokens.colorNeutralForegroundInverted
            }
          />
        }
        ariaLabel="Send"
        className={styles.sendButton}
        style={{
          background:
            query.length > QUERY_MAX_LENGTH || query.length === 0
              ? customTokens.colorNeutralBackgroundDisabled
              : "",
        }}
        disabled={query.length > QUERY_MAX_LENGTH}
        onClick={() => handleSend()}
      />
    );
  };

  const stopButton = () => {
    return (
      <IconButton
        icon={
          <Stop24Filled color={customTokens.colorNeutralForegroundInverted} />
        }
        ariaLabel="Stop"
        className={styles.sendButton}
        onClick={() => handleStop()}
      />
    );
  };

  const handleViewMore = () => {
    if (displayPromptOverlay) {
      const sqlQuery = extractLastBotSQL(message);
      getPromptsList(sqlQuery);

      dispatch(setTogglePromptOverlay(true));
    } else {
      dispatch(setVisibleCount(Math.min(6, 6)));
    }

    dispatch(setShowMore(true));
  };

  const handleHide = () => {
    if (displayPromptOverlay) {
      dispatch(setTogglePromptOverlay(false));
    }

    dispatch(setShowMore(false));
    dispatch(setVisibleCount(3));
  };

  const handleMobileViewMorePrompts = () => {
    dispatch(togglePromptDrawer(true));
  };

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {isMobile ? (
        <Button
          appearance="transparent"
          icon={<Prompt20Regular />}
          className={styles.viewMorePromptButton}
          onClick={() => handleMobileViewMorePrompts()}
          size={"small"}
        >
          View more prompts
        </Button>
      ) : (
        <Button
          appearance="transparent"
          icon={showMore ? <Dismiss16Regular /> : <Prompt20Regular />}
          className={styles.viewMorePromptButton}
          onClick={() => (!showMore ? handleViewMore() : handleHide())}
        >
          {showMore ? "Hide prompts" : "View more prompts"}
        </Button>
      )}

      <div className={styles.chatInputWrapper}>
        <div className={styles.chatInputContainer}>
          <div className={styles.topRow}>
            <Textarea
              className={styles.expandedInput}
              placeholder="Message AI Climate Assistant"
              value={query}
              onChange={(_, data) => dispatch(setQuery(data.value))}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  if (query.length > QUERY_MAX_LENGTH) {
                    e.preventDefault();
                    return;
                  }

                  handleSend();
                }
              }}
            />
          </div>

          <div className={styles.bottomRow}>
            <Typography
              variant="bodyXXRegular"
              color={
                query.length > QUERY_MAX_LENGTH
                  ? customTokens.colorStatusDangerForeground1
                  : customTokens.fontDarkGrayBody
              }
              className={styles.charCount}
            >
              {query.length}/{QUERY_MAX_LENGTH}
            </Typography>

            {query.length > QUERY_MAX_LENGTH ? (
              <div className={styles.errorText}>
                <ErrorCircle12Filled />
                <Typography
                  variant="bodyXXRegular"
                  color={customTokens.colorStatusDangerForeground1}
                >
                  Character limit exceeded
                </Typography>
              </div>
            ) : null}

            {loading ? stopButton() : sendButton()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InputBox;
